import * as React from 'react'
import { IconTile, type IconTileGradient, type IconTileSize, type IconTileTone } from './icon-tile'
import { cn } from '../../lib/utils'

/**
 * `EmptyState` — the one way the app says "there is nothing here"
 * (spec 2026-09-17-06).
 *
 * Before this existed the shape was documentation only (`patterns.tsx` §11)
 * and every list re-drew it: a dashed box here, a bare grey sentence there, a
 * centred lucide glyph at a third size somewhere else. The dashed box in
 * particular is the pattern that dates the app — a *drop target* is dashed,
 * an absence is not. So `EmptyState` draws a quiet surface instead.
 *
 * Two axes, and only two:
 *
 *  - `variant` is how much room it takes. `panel` is a list's or a page's
 *    empty body; `inline` is a cell, a side panel, a section inside a card —
 *    no background, less padding, and (spec 2026-09-24-01) a small, ungraded
 *    glyph — it sits next to populated content and must not outweigh it.
 *  - `tone` is how loud the glyph is. `soft` (the default) is a tinted tile
 *    with a coloured glyph, because most empty states are "nothing matches
 *    your filter". `solid` — the full gradient — is reserved for first-run
 *    states that invite the very first action, which is where spec
 *    2026-09-17-02's identity-surface rule puts gradients.
 */

export type EmptyStateVariant = 'panel' | 'inline'

export interface EmptyStateProps
    extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
    /** MDI path from `@mdi/js`. Omit it for a text-only absence. */
    icon?: string
    /** The one-line statement of what is missing. Already translated. */
    title?: React.ReactNode
    /** Why it is missing / what to do about it. Already translated. */
    description?: React.ReactNode
    /** A single primary control — `<Button size="sm">` — or nothing. */
    action?: React.ReactNode
    variant?: EmptyStateVariant
    tone?: IconTileTone
    gradient?: IconTileGradient
    /**
     * `true` on a "nothing matches these filters" state so a screen reader
     * announces it when the list re-renders. A first-run state is part of the
     * page from the start and needs no live region.
     */
    live?: boolean
}

export const EMPTY_STATE_VARIANT_CLASSES: Record<EmptyStateVariant, string> = {
    panel: 'rounded-xl bg-muted/40 px-6 py-10',
    inline: 'py-4',
}

/** Glyph weight per variant: `panel` is a page-level moment, `inline` sits
 * beside populated content and stays understated. */
const EMPTY_STATE_ICON_SIZE: Record<EmptyStateVariant, IconTileSize> = {
    panel: 'xl',
    inline: 'sm',
}

/** Colour per variant, unless the caller opts back into a louder glyph via
 * an explicit `gradient` prop. */
const EMPTY_STATE_DEFAULT_GRADIENT: Record<EmptyStateVariant, IconTileGradient> = {
    panel: 'brand',
    inline: 'neutral',
}

function EmptyState({
    icon,
    title,
    description,
    action,
    variant = 'panel',
    tone = 'soft',
    gradient,
    live,
    className,
    children,
    ...props
}: EmptyStateProps) {
    const resolvedGradient = gradient ?? EMPTY_STATE_DEFAULT_GRADIENT[variant]
    return (
        <div
            data-slot="empty-state"
            role={live ? 'status' : undefined}
            className={cn(
                'flex flex-col items-center justify-center gap-3 text-center',
                EMPTY_STATE_VARIANT_CLASSES[variant],
                className
            )}
            {...props}
        >
            {icon ? (
                <IconTile
                    path={icon}
                    size={EMPTY_STATE_ICON_SIZE[variant]}
                    tone={tone}
                    gradient={resolvedGradient}
                />
            ) : null}
            {title ? (
                <p className="text-sm font-medium text-foreground">{title}</p>
            ) : null}
            {description ? (
                <p className="max-w-[36ch] text-xs text-muted-foreground">{description}</p>
            ) : null}
            {children}
            {action ? <div className="flex items-center gap-2">{action}</div> : null}
        </div>
    )
}

export { EmptyState }
